import React, {useMemo, useState} from 'react';
import DashboardLayout from "../../../layouts/DashboardLayout";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {GET_MERCHANT_SUPPLIERS_LIST, GET_SUPPLIER_LIST} from "../../../utils/graphql";
import {apollo} from "../../../utils/apollo";
import Loading from "../../../components/Loading";
import SortIcon from "../../../components/SortIcon";
import StatusBadge from "../../../components/StatusBadge";
import {Link} from "react-router-dom";
import PaginationFull from "../../../components/PaginationFull";
import ButtonInternalLink from "../../../components/ButtonInternalLink";
import {UsersIcon, ChartBarIcon, UserPlusIcon, EyeIcon} from '@heroicons/react/24/outline';
import Button from "../../../components/Button";

function SubVendors() {
    const {t} = useTranslation();
    const [sort, setSort] = useState({field: 'id', isAscending: false});
    const [page, setPage] = useState(1);
    const [showFilter, setShowFilter] = useState(false);
    const tableHeader = [
        // {column: 'user_uuid', title: t('user_management.table.user_uuid'), sortable: true},
        {column: 'supplier_uuid', title: t('sub_vendors.table.supplierUuid'), sortable: false},
        {column: 'supplier_name', title: t('sub_vendors.table.supplierName'), sortable: false},
        {column: 'business_name', title: t('sub_vendors.table.businessName'), sortable: false},
        {column: 'email', title: t('sub_vendors.table.email'), sortable: false},
        {column: 'phone', title: t('sub_vendors.table.phone'), sortable: false},
        {column: 'active', title: t('sub_vendors.table.active'), sortable: false},
        {column: 'display_supplier_details', title: t('sub_vendors.table.displaySupplierDetails'), sortable: false},
        {column: 'actions', title: t('sub_vendors.table.actions'), sortable: false},
    ]

    const fetchSupplierList = useQuery(
        ['suppliers_list', {page, sort}],
        async () => {
            const {data} = await apollo.query({
                query: GET_SUPPLIER_LIST,
                variables: {
                    sortBy: sort.field?.toUpperCase(),
                    sortDirection: sort.isAscending ? "ASC" : "DESC",
                    page,
                    first: 15,
                    filter: {}
                },
            });
            return data?.getSupplierList;
        },
        {keepPreviousData: false}
    );
    const records = useMemo(() => fetchSupplierList?.isSuccess ? fetchSupplierList?.data : null, [fetchSupplierList]);

    const handleChangeSort = (field) => {
        if (sort.field === field) {
            setSort(prevState => ({...prevState, isAscending: !prevState.isAscending}));
        } else {
            setSort({field: field, isAscending: true});
        }
    };

    return (
        <DashboardLayout>
            <div>
                <div className="mb-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg bg-gradient-to-br from-green-500 to-blue-500 shadow-sm">
                            <UsersIcon className="h-6 w-6 text-white" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900">{t('sub_vendors.page_title')}</h2>
                    </div>
                    <p className="text-gray-600 mt-1">{t('sub_vendors.page_description')}</p>
                </div>
                
                <div className="flex justify-end mb-6">
                    <ButtonInternalLink
                        href="/merchant/sub-vendors/create"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gradient-to-br from-green-500 to-blue-500 hover:from-green-400 hover:to-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        {/*<UserPlusIcon className="h-5 w-5 mr-2" />*/}
                        {t('sub_vendors.create_sub_vendor')}
                    </ButtonInternalLink>
                </div>

                {showFilter && (
                    <div className="mt-3 shadow-md bg-white border rounded-lg px-4 py-2">
                        filter
                    </div>
                )}

                {fetchSupplierList.isLoading && (
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
                        <div className="flex justify-center items-center">
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="h-16 w-16 rounded-full bg-gradient-to-br from-green-500 to-blue-500 opacity-20 animate-ping"></div>
                                </div>
                                <div className="relative flex items-center justify-center">
                                    <div className="h-16 w-16 rounded-full bg-gradient-to-br from-green-500 to-blue-500 flex items-center justify-center">
                                        <UsersIcon className="h-8 w-8 text-white" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <h3 className="mt-6 text-lg font-semibold text-gray-900">{t('sub_vendors.loading_title') || 'Loading Sub-Vendors'}</h3>
                        <p className="mt-2 text-sm text-gray-500">{t('sub_vendors.loading_description') || 'Please wait while we fetch your sub-vendors data...'}</p>
                    </div>
                )}

                {(fetchSupplierList.isSuccess && (!records || !records?.data?.length)) && (
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
                        <div className="mx-auto h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center">
                            <ChartBarIcon className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="mt-6 text-lg font-semibold text-gray-900">{t('sub_vendors.no_data_title') || 'No Sub-Vendors Found'}</h3>
                        <p className="mt-2 text-sm text-gray-500">{t('sub_vendors.no_data_description') || 'You have not created any sub-vendors yet.'}</p>
                        <div className="mt-6">
                            <Button
                                onClick={() => fetchSupplierList.refetch()} 
                            >
                                {t('sub_vendors.refresh_data') || 'Refresh Data'}
                            </Button>
                        </div>
                    </div>
                )}

                {(fetchSupplierList.isSuccess && records && records?.data?.length > 0) && (
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 bg-gradient-to-br from-green-500 to-blue-500 border-b border-gray-200 rounded-t-xl">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded-lg bg-white/20">
                                        <UsersIcon className="h-5 w-5 text-white" />
                                    </div>
                                    <h3 className="text-lg font-medium text-white">{t('sub_vendors.sub_vendors_list') || 'Sub-Vendors List'}</h3>
                                </div>
                                <span className="text-xs font-medium text-white bg-white/20 px-3 py-1 rounded-full">
                                    {records?.data?.length || 0} {t('sub_vendors.records') || 'records'}
                                </span>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {tableHeader?.map(item => (
                                            <th 
                                                key={item.column} 
                                                scope="col" 
                                                className="px-6 py-3.5 ltr:text-left rtl:text-right text-xs font-semibold text-gray-700 uppercase tracking-wider"
                                            >
                                                {!item.hidden && (
                                                    <>
                                                        {item.sortable ? (
                                                            <button 
                                                                onClick={() => handleChangeSort(item.column)} 
                                                                type="button" 
                                                                className="group inline-flex items-center"
                                                            >
                                                                {item.title}
                                                                <SortIcon sort={sort} field={item.column}/>
                                                            </button>
                                                        ) : (
                                                            <span>{item.title}</span>
                                                        )}
                                                    </>
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {records?.data?.map((record) => (
                                        <tr key={record?.supplierUuid} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{record?.supplierUuid}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{record?.supplierName}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-500">{record?.businessName}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-500">{record?.email}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-500">{record?.phone}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {record?.active ? (
                                                    <div className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 inline-block">
                                                        Yes
                                                    </div>
                                                ) : (
                                                    <div className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200 inline-block">
                                                        No
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {record?.displaySupplierDetails ? (
                                                    <div className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 inline-block">
                                                        Yes
                                                    </div>
                                                ) : (
                                                    <div className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200 inline-block">
                                                        No
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <Link 
                                                    to={`/merchant/sub-vendors/view/${record?.supplierUuid}`}
                                                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                >
                                                    <EyeIcon className="h-4 w-4 ltr:mr-1 rtl:ml-1" />
                                                    View
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200">
                            <PaginationFull paginator={records?.paginatorInfo} get={page} set={setPage}/>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}

export default SubVendors;